// src/app/modules/Wallet/pages/WalletPage.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { ArrowDownLeft, Plus, Trash2, Check, Building2 } from 'lucide-react';
import * as actions from '../../Dashbord/_redux/dashboard/Actions';
import '../../Events/pages/events.scss';

const txTypeLabel = { credit: 'Credit', debit: 'Debit', payout: 'Payout', refund: 'Refund' };

export function WalletPage() {
  const dispatch = useDispatch();

  const { org } = useSelector(s => ({ org: s.auth.organization }), shallowEqual);
  const { wallet, transactions, bankAccounts, loading } = useSelector(s => ({
    wallet:       s.dashboard.wallet,
    transactions: s.dashboard.walletTransactions,
    bankAccounts: s.dashboard.bankAccounts,
    loading:      s.dashboard.actionsLoading,
  }), shallowEqual);

  const [showAddBank, setShowAddBank]   = useState(false);
  const [showPayout,  setShowPayout]    = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutAcct,   setPayoutAcct]   = useState('');
  const [bankForm, setBankForm] = useState({ bank_name: '', account_number: '', account_name: '' });

  useEffect(() => {
    if (!org) return;
    dispatch(actions.fetchAllData('wallet', org.organization_id));
    dispatch(actions.fetchAllData('wallet_transactions', org.organization_id, { limit: 20 }));
    dispatch(actions.fetchAllData('bank_accounts', org.organization_id));
  }, [org]);

  const balance = wallet?.available_balance ?? wallet?.balance ?? 0;

  const handleAddBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_number || !bankForm.account_name) return;
    try {
      await dispatch(actions.addData('add_bank_account', { ...bankForm, organization_id: org.organization_id }));
      setBankForm({ bank_name: '', account_number: '', account_name: '' });
      setShowAddBank(false);
      dispatch(actions.fetchAllData('bank_accounts', org.organization_id));
    } catch (_) {}
  };

  const handleRemoveBank = (id) => {
    if (!window.confirm('Remove this bank account?')) return;
    dispatch(actions.addData('remove_bank_account', {}, id)).then(() => {
      dispatch(actions.fetchAllData('bank_accounts', org.organization_id));
    });
  };

  const handleSetDefault = (id) => {
    dispatch(actions.addData('set_default_bank_account', {}, id)).then(() => {
      dispatch(actions.fetchAllData('bank_accounts', org.organization_id));
    });
  };

  const handlePayout = async () => {
    if (!payoutAmount || !payoutAcct) return;
    try {
      await dispatch(actions.addData('request_payout', {
        amount:          Number(payoutAmount),
        bank_account_id: payoutAcct,
        organization_id: org.organization_id,
      }));
      setPayoutAmount('');
      setPayoutAcct('');
      setShowPayout(false);
      dispatch(actions.fetchAllData('wallet', org.organization_id));
      dispatch(actions.fetchAllData('wallet_transactions', org.organization_id, { limit: 20 }));
    } catch (_) {}
  };

  return (
    <div className="ev-page">
      <div className="ev-page__header">
        <div>
          <h1 className="ev-page__title">Wallet</h1>
          <p className="ev-page__sub">Manage your earnings and payouts</p>
        </div>
      </div>

      {/* Balance card */}
      <div className="ev-wallet-balance">
        <div className="ev-wallet-balance__label">Available Balance</div>
        <div className="ev-wallet-balance__amount">₦{Number(balance).toLocaleString()}</div>
        <div className="ev-wallet-balance__actions">
          <button
            className="ev-wallet-balance__action-btn"
            onClick={() => setShowPayout(true)}
            disabled={!bankAccounts?.length || balance <= 0}
          >
            <ArrowDownLeft size={14} /> Request Payout
          </button>
        </div>
      </div>

      {/* Bank accounts */}
      <div className="ev-section" style={{ marginBottom: 20 }}>
        <div className="ev-section__head">
          <span className="ev-section__title">Bank Accounts</span>
          <button className="ev-btn ev-btn--outline" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => setShowAddBank(true)}>
            <Plus size={13} /> Add Account
          </button>
        </div>
        {(!bankAccounts || bankAccounts.length === 0) && (
          <div className="ev-empty" style={{ padding: '28px 24px' }}>
            <Building2 size={28} strokeWidth={1.4} />
            <p>No bank accounts added yet.</p>
          </div>
        )}
        {bankAccounts?.map(acct => (
          <div className="ev-bank-card" key={acct.bank_account_id}>
            <div className="ev-bank-card__info">
              <div className="ev-bank-card__name">{acct.bank_name}</div>
              <div className="ev-bank-card__acct">{acct.account_number} · {acct.account_name}</div>
            </div>
            <div className="ev-bank-card__actions">
              {acct.is_default ? (
                <span className="ev-bank-card__default">Default</span>
              ) : (
                <button className="ev-btn ev-btn--outline" style={{ padding: '5px 10px', fontSize: 11.5 }} onClick={() => handleSetDefault(acct.bank_account_id)}>
                  <Check size={12} /> Set Default
                </button>
              )}
              <button className="ev-btn ev-btn--ghost" onClick={() => handleRemoveBank(acct.bank_account_id)}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Transaction history */}
      <div className="ev-section">
        <div className="ev-section__head">
          <span className="ev-section__title">Transaction History</span>
        </div>
        <div className="ev-tx-head">
          <span>Description</span>
          <span>Type</span>
          <span>Date</span>
          <span>Amount</span>
        </div>
        {(!transactions || transactions.length === 0) && (
          <div className="ev-empty" style={{ padding: '28px 24px' }}>
            <p>No transactions yet.</p>
          </div>
        )}
        {transactions?.map((tx, i) => {
          const isCredit = ['credit', 'refund'].includes(tx.type);
          return (
            <div className="ev-tx-row" key={tx.transaction_id || i}>
              <span className="ev-tx-row__type">{tx.description || txTypeLabel[tx.type] || tx.type}</span>
              <span>{txTypeLabel[tx.type] || tx.type}</span>
              <span>{tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-NG') : '—'}</span>
              <span className={isCredit ? 'ev-tx-row__amount-credit' : 'ev-tx-row__amount-debit'}>
                {isCredit ? '+' : '-'}₦{tx.amount ? Number(tx.amount).toLocaleString() : '0'}
              </span>
            </div>
          );
        })}
      </div>

      {/* Add bank dialog */}
      {showAddBank && (
        <div className="ev-overlay">
          <div className="ev-dialog">
            <h3>Add Bank Account</h3>
            {[
              { key: 'bank_name',       label: 'Bank Name',      placeholder: 'e.g. GTBank' },
              { key: 'account_number',  label: 'Account Number', placeholder: '0123456789' },
              { key: 'account_name',    label: 'Account Name',   placeholder: 'John Doe' },
            ].map(({ key, label, placeholder }) => (
              <div className="ev-field" key={key} style={{ marginBottom: 12 }}>
                <label className="ev-field__label">{label}</label>
                <input
                  className="ev-input"
                  placeholder={placeholder}
                  value={bankForm[key]}
                  onChange={e => setBankForm(f => ({ ...f, [key]: e.target.value }))}
                />
              </div>
            ))}
            <div className="ev-dialog__actions">
              <button className="ev-btn ev-btn--outline" onClick={() => setShowAddBank(false)}>Cancel</button>
              <button className="ev-btn ev-btn--primary" onClick={handleAddBank} disabled={loading}>Add Account</button>
            </div>
          </div>
        </div>
      )}

      {/* Payout dialog */}
      {showPayout && (
        <div className="ev-overlay">
          <div className="ev-dialog">
            <h3>Request Payout</h3>
            <div className="ev-field" style={{ marginBottom: 12 }}>
              <label className="ev-field__label">Bank Account</label>
              <select className="ev-input" value={payoutAcct} onChange={e => setPayoutAcct(e.target.value)}>
                <option value="">Select account</option>
                {bankAccounts?.map(a => (
                  <option key={a.bank_account_id} value={a.bank_account_id}>
                    {a.bank_name} · {a.account_number}
                  </option>
                ))}
              </select>
            </div>
            <div className="ev-field" style={{ marginBottom: 20 }}>
              <label className="ev-field__label">Amount (₦)</label>
              <input
                type="number"
                className="ev-input"
                placeholder={`Max ₦${Number(balance).toLocaleString()}`}
                value={payoutAmount}
                onChange={e => setPayoutAmount(e.target.value)}
                min={1}
                max={balance}
              />
            </div>
            <div className="ev-dialog__actions">
              <button className="ev-btn ev-btn--outline" onClick={() => setShowPayout(false)}>Cancel</button>
              <button className="ev-btn ev-btn--primary" onClick={handlePayout} disabled={loading || !payoutAcct || !payoutAmount}>
                {loading ? 'Processing…' : 'Request Payout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
